import { useMutation, useQuery } from "@apollo/client";
import { Plus, Tag } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast as sonnerToast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { GetDiscountsQuery } from "@/gql/graphql";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { DeleteConfirmationDialog } from "@/lib/components/delete-confirmation-dialog";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import { DiscountForm } from "../components/discount-form";
import { DiscountList } from "../components/discount-list";
import { DiscountMethodSelectionDialog } from "../components/discount-method-selection-dialog";
import { CREATE_DISCOUNT, DELETE_DISCOUNT, UPDATE_DISCOUNT } from "../graphql/mutations";
import { GET_DISCOUNTS } from "../graphql/queries";
import type { DiscountFormData, DiscountRuleFormData } from "../types";

type GqlDiscount = GetDiscountsQuery["getDiscounts"][number];

export function DiscountsPage() {
  const { t } = useTranslation("discounts");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const toast = sonnerToast;
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const salonId = currentSalon?.id;
  const [showMethodSelection, setShowMethodSelection] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<"coupon" | "automatic" | null>(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<GqlDiscount | null>(null);
  const [activeTab, setActiveTab] = useState<"active" | "history">("active");

  // Fetch discounts
  const {
    data,
    loading: loadingDiscounts,
    refetch,
  } = useQuery(GET_DISCOUNTS, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
  });

  const allDiscounts = data?.getDiscounts ?? [];

  // Filter discounts based on active tab and timezone
  // "active" = current + scheduled (not yet expired)
  // "history" = expired
  const getFilteredDiscounts = () => {
    const salonTimezone = currentSalon?.timezone ?? "America/Los_Angeles";
    const now = DateTime.now().setZone(salonTimezone);

    return allDiscounts.filter((discount) => {
      const activePeriod = discount.conditions?.timeCondition?.activePeriod;
      const targetedPeriod = discount.conditions?.timeCondition?.targetedPeriod;
      const endDatetime = activePeriod?.endDatetime ?? targetedPeriod?.endDatetime;

      if (!endDatetime) {
        return activeTab === "active";
      }

      const endDate = DateTime.fromISO(endDatetime, { zone: "utc" }).setZone(salonTimezone);

      const isExpired = endDate < now;

      return activeTab === "active" ? !isExpired : isExpired;
    });
  };

  const discounts = getFilteredDiscounts();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState<GqlDiscount | null>(null);

  const [createDiscount, { loading }] = useMutation(CREATE_DISCOUNT, {
    onCompleted: (data) => {
      toast(t("notifications.created.title"), {
        description: data.createDiscount.code
          ? t("notifications.created.descriptionWithCode", { code: data.createDiscount.code })
          : t("notifications.created.description"),
      });
      setShowDiscountForm(false);
      setEditingDiscount(null);
      refetch();
    },
    onError: (error) => {
      toast(t("notifications.error.title"), {
        description: error.message,
      });
    },
  });

  const [updateDiscount, { loading: updateLoading }] = useMutation(UPDATE_DISCOUNT, {
    onCompleted: () => {
      toast(t("notifications.updated.title"), {
        description: t("notifications.updated.description"),
      });
      setShowDiscountForm(false);
      setEditingDiscount(null);
      refetch();
    },
    onError: (error) => {
      toast(t("notifications.error.title"), {
        description: error.message,
      });
    },
  });

  const [deleteDiscount, { loading: deleteLoading }] = useMutation(DELETE_DISCOUNT, {
    onCompleted: () => {
      toast(t("notifications.deleted.title"), {
        description: t("notifications.deleted.description"),
      });
      setDeleteDialogOpen(false);
      setDiscountToDelete(null);
      refetch();
    },
    onError: (error) => {
      toast(t("notifications.error.title"), {
        description: error.message,
      });
    },
  });

  const handleAddDiscount = () => {
    setEditingDiscount(null);
    setSelectedMethod(null);
    setShowMethodSelection(true);
  };

  const handleMethodSelect = (method: "coupon" | "automatic") => {
    setSelectedMethod(method);
    setShowDiscountForm(true);
  };

  const handleFormClose = () => {
    setShowDiscountForm(false);
    setSelectedMethod(null);
  };

  const convertDiscountToFormData = (discount: GqlDiscount): Partial<DiscountFormData> => {
    const conditions = discount.conditions;
    const rules = discount.rules;
    const activePeriod = conditions?.timeCondition?.activePeriod;
    const targetedPeriod = conditions?.timeCondition?.targetedPeriod;

    const salonTimezone = currentSalon?.timezone ?? "America/Los_Angeles";

    // Convert GQL rules to form rules
    const formRules: DiscountRuleFormData[] = rules?.items?.map((item) => ({
      valueType: (item.valueType?.toLowerCase() ?? "percentage") as "percentage" | "fixed",
      value: item.value ?? 0,
      targetType: (item.target.type?.toLowerCase() ?? "all_services") as
        | "all_services"
        | "specific_services",
      selectedServiceIds: item.target.serviceIds ?? [],
    })) ?? [
      { valueType: "percentage", value: 0, targetType: "all_services", selectedServiceIds: [] },
    ];

    return {
      autoApply: discount.autoApply,
      title: discount.name ?? "",
      code: discount.code ?? "",
      couponType: (discount.couponType?.toLowerCase() ?? "public") as "public" | "private",
      applyTo: (rules?.applyTo?.toLowerCase() ?? "per_item") as "per_item" | "total",
      rules: formRules,
      minSpent: discount.minSpent ?? undefined,
      startDateTime: activePeriod?.startDatetime
        ? DateTime.fromISO(activePeriod.startDatetime, { zone: "utc" }).setZone(salonTimezone)
        : targetedPeriod?.startDatetime
          ? DateTime.fromISO(targetedPeriod.startDatetime, { zone: "utc" }).setZone(salonTimezone)
          : DateTime.now().startOf("day"),
      endDateTime: activePeriod?.endDatetime
        ? DateTime.fromISO(activePeriod.endDatetime, { zone: "utc" }).setZone(salonTimezone)
        : targetedPeriod?.endDatetime
          ? DateTime.fromISO(targetedPeriod.endDatetime, { zone: "utc" }).setZone(salonTimezone)
          : DateTime.now().endOf("day"),
      hasTimeInterval: !!(targetedPeriod?.startDatetime && targetedPeriod?.endDatetime),
      // Booking period: use targetedPeriod if set, otherwise fall back to activePeriod
      targetStartDateTime: targetedPeriod?.startDatetime
        ? DateTime.fromISO(targetedPeriod.startDatetime, { zone: "utc" }).setZone(salonTimezone)
        : activePeriod?.startDatetime
          ? DateTime.fromISO(activePeriod.startDatetime, { zone: "utc" }).setZone(salonTimezone)
          : undefined,
      targetEndDateTime: targetedPeriod?.endDatetime
        ? DateTime.fromISO(targetedPeriod.endDatetime, { zone: "utc" }).setZone(salonTimezone)
        : activePeriod?.endDatetime
          ? DateTime.fromISO(activePeriod.endDatetime, { zone: "utc" }).setZone(salonTimezone)
          : undefined,
      hasOffPeakHours: !!(targetedPeriod?.offPeakHours && targetedPeriod.offPeakHours.length > 0),
      offPeakHours:
        (targetedPeriod?.offPeakHours?.length ?? 0) > 0
          ? targetedPeriod?.offPeakHours?.map((schedule) => ({
              dayOfWeek: parseInt(schedule.dayOfWeek, 10),
              dayName: [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ][parseInt(schedule.dayOfWeek, 10)],
              timeSlots:
                schedule.timeRanges?.map((range: string, idx: number) => {
                  const [start, end] = range.split("-");
                  return {
                    id: `slot-${idx}-${Date.now()}`,
                    startTime: start,
                    endTime: end,
                  };
                }) ?? [],
            }))
          : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
              (dayName, index) => ({
                dayOfWeek: index,
                dayName,
                timeSlots: [],
              })
            ),
      isActive: discount.isActive,
    };
  };

  const handleFormSubmit = async (data: DiscountFormData) => {
    if (!salonId) {
      toast(t("notifications.error.title"), {
        description: "Salon ID is missing",
      });
      return;
    }

    // Transform rules to GraphQL input
    const toEnumValueType = (v: string): "FIXED" | "PERCENTAGE" =>
      v === "fixed" ? "FIXED" : "PERCENTAGE";
    const toEnumTargetType = (t: string): "SPECIFIC_SERVICES" | "ALL_SERVICES" =>
      t === "specific_services" ? "SPECIFIC_SERVICES" : "ALL_SERVICES";
    const toEnumApplyTo = (a: string): "TOTAL" | "PER_ITEM" =>
      a === "total" ? "TOTAL" : "PER_ITEM";
    const toEnumCouponType = (c?: string): "PRIVATE" | "PUBLIC" | undefined =>
      c === "private" ? "PRIVATE" : c === "public" ? "PUBLIC" : undefined;

    const rulesInput = data.rules.map((rule) => ({
      valueType: toEnumValueType(rule.valueType),
      value: rule.value,
      target: {
        type: toEnumTargetType(rule.targetType),
        serviceIds: rule.targetType === "specific_services" ? rule.selectedServiceIds : undefined,
      },
    }));

    // Booking period (targetStartDateTime/targetEndDateTime) is ALWAYS required
    const targetStartDateTime = data.targetStartDateTime ?? data.startDateTime;
    const targetEndDateTime = data.targetEndDateTime ?? data.endDateTime;

    const baseInput = {
      title: data.title,
      code: data.code,
      couponType: toEnumCouponType(data.couponType),
      applyTo: toEnumApplyTo(data.applyTo),
      rules: rulesInput,
      minSpent: data.minSpent ?? null,
      // activePeriod = visibility window on storefront (only when explicitly set)
      //   null clears it on update; omitted on create means no visibility restriction
      // targetedPeriod = booking period (when bookings must fall to qualify)
      activePeriod: data.hasTimeInterval
        ? {
            startDateTime: data.startDateTime.toUTC().toISO() ?? "",
            endDateTime: data.endDateTime.toUTC().toISO() ?? "",
          }
        : null,
      targetedPeriod: {
        startDateTime: targetStartDateTime.toUTC().toISO() ?? undefined,
        endDateTime: targetEndDateTime.toUTC().toISO() ?? undefined,
        ...(data.hasOffPeakHours
          ? {
              offPeakHours: data.offPeakHours?.map((d) => ({
                dayOfWeek: String(d.dayOfWeek),
                timeRanges: d.timeSlots.map((s) => `${s.startTime}-${s.endTime}`),
              })),
            }
          : {}),
      },
      isActive: data.isActive,
    };

    if (editingDiscount) {
      await updateDiscount({
        variables: {
          salonId,
          discountId: editingDiscount.id,
          input: baseInput,
        },
      });
    } else {
      await createDiscount({
        variables: {
          salonId,
          input: { ...baseInput, autoApply: data.autoApply },
        },
      });
    }
  };

  return (
    <>
      <BaseNavigationBar
        title={t("pageTitle")}
        subtitle={t("pageSubtitle")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <Button key="add-discount" onClick={handleAddDiscount} size="sm">
            <Plus className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">{t("actions.addDiscount")}</span>
          </Button>,
        ]}
      />

      <PageContent>
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "active" | "history")}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="active">{t("tabs.active")}</TabsTrigger>
            <TabsTrigger value="history">{t("tabs.history")}</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-0">
            {loadingDiscounts ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">{t("common:loading")}</div>
              </div>
            ) : discounts.length > 0 ? (
              <>
                <DiscountList
                  discounts={discounts}
                  salonTimezone={currentSalon?.timezone ?? "America/Los_Angeles"}
                  onClick={(discount) => {
                    setEditingDiscount(discount);
                    setShowDiscountForm(true);
                  }}
                  onEdit={(discount) => {
                    setEditingDiscount(discount);
                    setShowDiscountForm(true);
                  }}
                  onDelete={(discount) => {
                    setDiscountToDelete(discount);
                    setDeleteDialogOpen(true);
                  }}
                />
                <DeleteConfirmationDialog
                  open={deleteDialogOpen}
                  onOpenChange={(open) => {
                    setDeleteDialogOpen(open);
                    if (!open) setDiscountToDelete(null);
                  }}
                  description={t("notifications.confirmDelete", { name: discountToDelete?.name })}
                  confirmText={t("list.actions.delete") ?? undefined}
                  cancelText={t("actions.cancel") ?? undefined}
                  onConfirm={() => {
                    if (!salonId || !discountToDelete) return;
                    deleteDiscount({ variables: { salonId, discountId: discountToDelete.id } });
                  }}
                  isLoading={deleteLoading}
                />
              </>
            ) : allDiscounts.length === 0 ? (
              <Card className="border-dashed">
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Tag className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-xl">{t("emptyState.title")}</CardTitle>
                  <CardDescription className="max-w-sm mx-auto">
                    {t("emptyState.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center pb-6">
                  <Button onClick={handleAddDiscount}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t("actions.addDiscount")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">{t("emptyState.noDiscountsInTab")}</div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </PageContent>

      {/* Method Selection Dialog */}
      <DiscountMethodSelectionDialog
        open={showMethodSelection}
        onOpenChange={setShowMethodSelection}
        onMethodSelect={handleMethodSelect}
      />

      {/* Discount Form */}
      <DiscountForm
        open={showDiscountForm}
        onOpenChange={(open) => {
          if (!open) {
            handleFormClose();
          }
        }}
        onSubmit={handleFormSubmit}
        loading={loading || updateLoading}
        initialData={
          editingDiscount
            ? convertDiscountToFormData(editingDiscount)
            : selectedMethod
              ? { autoApply: selectedMethod === "automatic" }
              : undefined
        }
        isEditing={!!editingDiscount}
      />
    </>
  );
}
